import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { getAccessiblePersonas, type PersonaRoute } from '../utils/accessControl';

interface ForbiddenPageProps {
  message?: string;
  showTenantSwitcher?: boolean;
}

const personaLabels: Record<PersonaRoute, string> = {
  'self-care': 'Self-Care',
  assist: 'Assist',
  admin: 'Admin',
};

export const ForbiddenPage = ({
  message = "You don't have permission to access this page.",
  showTenantSwitcher = true,
}: ForbiddenPageProps) => {
  const navigate = useNavigate();
  const { currentTenant, availableTenants, switchTenant } = useTenant();
  const [switchingTenantId, setSwitchingTenantId] = useState<string | null>(null);

  // Get accessible personas for current tenant
  const accessiblePersonas = currentTenant ? getAccessiblePersonas(currentTenant.roles) : [];

  const otherTenants = availableTenants.filter(
    (t) => t.tenantId !== currentTenant?.tenantId,
  );

  // Two *different* tenants can legitimately share a display name
  // (memberships are already deduped by tenant_id), so a bare name can't
  // disambiguate two "Switch to X" buttons. Mirrors the discriminator
  // added to the Login picker and manage's ForbiddenPage (EL-806) for the
  // same ambiguous membership shape.
  const nameCounts = new Map<string, number>();
  otherTenants.forEach((t) =>
    nameCounts.set(t.tenantName, (nameCounts.get(t.tenantName) ?? 0) + 1)
  );

  const handlePersonaClick = (persona: PersonaRoute) => {
    navigate(`/${persona}`);
  };

  const handleSwitchToTenant = async (tenantId: string) => {
    setSwitchingTenantId(tenantId);
    try {
      await switchTenant(tenantId);
    } catch {
      setSwitchingTenantId(null);
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/session/logout', {
        method: 'POST',
        credentials: 'include',
      });
      localStorage.removeItem('elli-current-tenant-id');
      localStorage.removeItem('elli-user-profile');
      localStorage.removeItem('elli-sidebar-collapsed');
    } catch {
      // Ignore errors
    }
    window.location.href = '/';
  };

  return (
    <div className="standalone-page fixed inset-0 flex items-center justify-center bg-gradient-to-br from-[#f5f7fa] to-[#e4e8ed] px-6 py-10 z-[1000]">
      <div className="bg-card p-12 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] max-w-[500px] w-full text-center">
        <div className="text-[64px] mb-6">🚫</div>
        <h1 className="font-display text-[28px] font-extrabold tracking-[-0.015em] text-foreground m-0">Access Denied</h1>
        <p className="text-muted-foreground mb-8 leading-relaxed text-base">{message}</p>

        {/* Show accessible personas */}
        {accessiblePersonas.length > 0 && (
          <div className="mb-6">
            <p className="text-muted-foreground mb-3 text-sm">You can access:</p>
            <div className="flex flex-col gap-3">
              {accessiblePersonas.map((persona) => (
                <button
                  key={persona}
                  onClick={() => handlePersonaClick(persona)}
                  className="text-base font-semibold py-3.5 px-6 rounded-lg border-none cursor-pointer transition-all duration-200 ease-in-out bg-primary text-primary-foreground hover:bg-primary/85"
                >
                  Go to {personaLabels[persona]}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tenant switcher */}
        {showTenantSwitcher && otherTenants.length > 0 && (
          <>
            <div className="my-6 border-t border-border" />
            <p className="text-muted-foreground mb-3 text-sm">
              You may have different permissions in another workspace.
            </p>
            <div className="flex flex-col gap-3">
              {otherTenants.map((tenant) => {
                const isDuplicate = (nameCounts.get(tenant.tenantName) ?? 0) > 1;
                const switchLabel = isDuplicate
                  ? `Switch to ${tenant.tenantName} (#${tenant.tenantId.slice(-8)})`
                  : `Switch to ${tenant.tenantName}`;
                return (
                  <button
                    key={tenant.tenantId}
                    onClick={() => handleSwitchToTenant(tenant.tenantId)}
                    disabled={switchingTenantId !== null}
                    className="text-sm font-medium py-3 px-6 rounded-lg border border-border cursor-pointer transition-all duration-200 ease-in-out bg-card text-foreground hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {switchingTenantId === tenant.tenantId ? 'Switching...' : switchLabel}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* No access at all */}
        {accessiblePersonas.length === 0 && (
          <p className="text-muted-foreground text-sm mt-4">
            Please contact your administrator for access.
          </p>
        )}

        {/* Action buttons - always visible */}
        <div className="mt-6 pt-6 border-t border-border flex flex-col gap-3">
          <button
            onClick={handleGoBack}
            className="text-sm font-medium py-3 px-6 rounded-lg border border-border cursor-pointer transition-all duration-200 ease-in-out bg-card text-foreground hover:bg-muted"
          >
            Go Back
          </button>
          <button
            onClick={handleLogout}
            className="text-sm font-medium py-3 px-6 rounded-lg border border-border cursor-pointer transition-all duration-200 ease-in-out bg-card text-muted-foreground hover:bg-muted"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForbiddenPage;
